import { useState, useEffect, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"
import { ArrowLeft, Save } from "lucide-react"
import { toast } from 'sonner'
import { useUserRol, useUserName, useTienePermiso} from '../App';
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select"
import { RechazoModal } from "./RechazoModal";

interface NMXA72112FormData {
  // Información general
  fecha: string
  hora: string
  temperatura: string
  humedad: string
  especimenes_ensayados: string
  // especimenes_ensayados_trama?: string // <-- remove duplicate
  distancia_medicion: string
  urdimbre: string
  trama: string
  
  // Mediciones - Urdimbre
  no1_urdimbre: string
  no2_urdimbre: string
  no3_urdimbre: string
  no4_urdimbre: string
  no5_urdimbre: string
  
  // Mediciones - Trama
  no1_trama: string
  no2_trama: string
  no3_trama: string
  no4_trama: string
  no5_trama: string
  
  // Promedios y resultados
  promedio_urdimbre: string
  promedio_trama: string
  resultado_urdimbre: string
  resultado_trama: string
  
  // Personal
  realizo: string
  superviso: string
  
  // Observaciones
  observaciones: string
  mostrarObservaciones: boolean

  // Nuevos campos
  numero_solicitud?: string;
  id_termohigrometro?: string;
  especimenes_ensayados_trama?: string;
  desviacion_metodo?: string;
}

interface NMXA72112FormProps {
  mode: "create" | "view" | "edit"
  initialData?: Partial<NMXA72112FormData>
  onSave?: (data: NMXA72112FormData) => void
  onCancel?: () => void
  pedidoId?: string
  pedidoRowId?: string
  realizoUser?: string
  onRefreshBackend?: () => void // <-- NUEVO
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

const DISTANCIAS = ["2 cm", "3 cm", "5 cm", "10 cm"];
const TERM_OPTIONS = ["E-59", "E-57", "E-56"];

export function NMXA72112Form({
  mode,
  initialData,
  onSave,
  onCancel,
  pedidoId,
  pedidoRowId,
  realizoUser,
  onRefreshBackend, // <-- NUEVO
  onAutosave,
}: NMXA72112FormProps) {
  const globalRol = useUserRol();
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const userName = useUserName();
  const effectiveRol = globalRol;

  const [formData, setFormData] = useState<NMXA72112FormData>({
    fecha: "",
    hora: "",
    temperatura: "",
    humedad: "",
    especimenes_ensayados: "",
    especimenes_ensayados_trama: "",
    distancia_medicion: "",
    urdimbre: "",
    trama: "",
    no1_urdimbre: "",
    no2_urdimbre: "",
    no3_urdimbre: "",
    no4_urdimbre: "",
    no5_urdimbre: "",
    no1_trama: "",
    no2_trama: "",
    no3_trama: "",
    no4_trama: "",
    no5_trama: "",
    promedio_urdimbre: "",
    promedio_trama: "",
    resultado_urdimbre: "",
    resultado_trama: "",
    realizo: "",
    superviso: "",
    observaciones: "",
    desviacion_metodo: initialData?.desviacion_metodo || "",
    ...initialData,
    mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
  })

  const [editMode, setEditMode] = useState(false);
  const [estatus, setEstatus] = useState<string>(initialData && (initialData as any).ACEPTADO ? (initialData as any).ACEPTADO : '');
  const [rechazoModalOpen, setRechazoModalOpen] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    setFormData(prev => ({ ...prev, ...initialData, mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true }))
    setEstatus((initialData as any)?.ACEPTADO || '')
    setEditMode(false)
  }, [initialData])

  useEffect(() => {
    if (mode === 'create' && realizoUser) {
      setFormData(prev => ({ ...prev, realizo: realizoUser }))
    }
  }, [mode, realizoUser])

  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false)
    }
  }, [effectiveRol, mode])

  const isReadOnly = mode === 'create' ? false : (mode === 'view' && !editMode)

  // --- NUEVO: Acciones aceptar/rechazar/editar ---
  const [autosaveRowId, setAutosaveRowId] = useState<string>((initialData as any)?.ROW_ID || (initialData as any)?.row_id || '');
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialData as any)?.nomId || '');
  const rowId = autosaveRowId;
  const nomId = autosaveNomId;

  const handleAccion = async (accion: 'aceptado' | 'rechazado', razonRechazo?: string) => {
    const supervisorName = userName || formData.superviso || '';
    if (accion === 'rechazado' && !razonRechazo) {
      setRechazoModalOpen(true);
      return;
    }
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-7211/2?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-7211/2?';
    if (!window.confirm(confirmMsg)) return;
    const payload = {
      ...formData,
      superviso: supervisorName,
      ROW_ID: (initialData as any)?.ROW_ID || (initialData as any)?.row_id || '',
      PEDIDO: pedidoId,
      PRUEBA: (initialData as any)?.PRUEBA || 'NMX-A-7211/2-INNTEX-2015 DENSIDAD DE TEJIDO PLANO (MÉTODO A)',
      SUPERVISO: supervisorName,
      usuario: supervisorName,
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion,
      desviacion_metodo: formData.desviacion_metodo,
      ...(accion === 'rechazado' ? { RAZON_RECHAZO: razonRechazo } : {})
    }
    try {
      if (accion === 'rechazado') setRechazoLoading(true);
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, accion === 'rechazado' ? razonRechazo : undefined, supervisorName, nomId);
      setEstatus(payload.ACEPTAR)
      setFormData(prev => ({ ...prev, superviso: supervisorName }))
      setEditMode(false)
      setRechazoModalOpen(false);
      setRechazoLoading(false);
      toast.success(`Prueba ${payload.ACEPTAR === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      setRechazoLoading(false);
      alert('Error al enviar al backend: ' + (err.message || err))
    }
  }

  const handleEditar = () => {
    setEditMode(true)
  }

  const handleGuardarEdicion = async () => {
    const supervisorName = userName || formData.superviso || '';
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-7211/2? Esta acción no se puede deshacer.')) return;
    const payload = {
      ...formData,
      superviso: supervisorName,
      ROW_ID: (initialData as any)?.ROW_ID || (initialData as any)?.row_id || '',
      PEDIDO: pedidoId,
      PRUEBA: (initialData as any)?.PRUEBA || 'NMX-A-7211/2-INNTEX-2015 DENSIDAD DE TEJIDO PLANO (MÉTODO A)',
      SUPERVISO: supervisorName,
      usuario: supervisorName,
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado',
      desviacion_metodo: formData.desviacion_metodo
    }
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  }

  // Lógica robusta para saber si la prueba está aceptada o rechazada
  const aceptadoRaw = (initialData as any)?.ACEPTADO ?? (initialData as any)?.aceptado;
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : '';
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado' || estatus === 'aceptado' || estatus === 'rechazado';

  const truncate1 = (num: number) => Math.trunc(num * 10) / 10;

  const handleInputChange = (field: keyof NMXA72112FormData, value: any) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // Calcular promedios automáticamente
      if (["no1_urdimbre", "no2_urdimbre", "no3_urdimbre", "no4_urdimbre", "no5_urdimbre"].includes(field)) {
        const valores = [
          parseFloat(newData.no1_urdimbre) || 0,
          parseFloat(newData.no2_urdimbre) || 0,
          parseFloat(newData.no3_urdimbre) || 0,
          parseFloat(newData.no4_urdimbre) || 0,
          parseFloat(newData.no5_urdimbre) || 0
        ].filter(v => v > 0);
        if (valores.length > 0) {
          const promedio = valores.reduce((a, b) => a + b, 0) / valores.length;
          newData.promedio_urdimbre = promedio.toFixed(1);
          // Calcular resultado automático si distancia seleccionada
          const dist = parseFloat(newData.urdimbre);
          if (dist) {
            // Truncar a un decimal (no redondear)
            const resultado = Math.trunc((promedio / dist) * 10) / 10;
            newData.resultado_urdimbre = resultado ? resultado.toFixed(1) : "";
          } else {
            newData.resultado_urdimbre = "";
          }
        }
      }
      if (["no1_trama", "no2_trama", "no3_trama", "no4_trama", "no5_trama"].includes(field)) {
        const valores = [
          parseFloat(newData.no1_trama) || 0,
          parseFloat(newData.no2_trama) || 0,
          parseFloat(newData.no3_trama) || 0,
          parseFloat(newData.no4_trama) || 0,
          parseFloat(newData.no5_trama) || 0
        ].filter(v => v > 0);
        if (valores.length > 0) {
          const promedio = valores.reduce((a, b) => a + b, 0) / valores.length;
          newData.promedio_trama = promedio.toFixed(1);
          // Calcular resultado automático si distancia seleccionada
          const dist = parseFloat(newData.trama);
          if (dist) {
            // Truncar a un decimal (no redondear)
            const resultado = Math.trunc((promedio / dist) * 10) / 10;
            newData.resultado_trama = resultado ? resultado.toFixed(1) : "";
          } else {
            newData.resultado_trama = "";
          }
        }
      }
      // Si cambia la distancia, recalcula resultado
      if (field === "urdimbre") {
        const promedio = parseFloat(newData.promedio_urdimbre) || 0;
        const dist = parseFloat(value) || 0;
        // Truncar a un decimal (no redondear)
        const resultado = dist ? Math.trunc((promedio / dist) * 10) / 10 : 0;
        newData.resultado_urdimbre = dist && resultado ? resultado.toFixed(1) : "";
      }
      if (field === "trama") {
        const promedio = parseFloat(newData.promedio_trama) || 0;
        const dist = parseFloat(value) || 0;
        // Truncar a un decimal (no redondear)
        const resultado = dist ? Math.trunc((promedio / dist) * 10) / 10 : 0;
        newData.resultado_trama = dist && resultado ? resultado.toFixed(1) : "";
      }
      return newData;
    });
  };

  const formPayload = () => formData;

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-7211/2-INNTEX-2015 DENSIDAD DE TEJIDO PLANO (METODO A)',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onCancel?.()
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) {
        onSave(formData)
      }
    } finally {
      setGuardando(false)
    }
  }

  const getTitle = () => {
    switch (mode) {
      case "create":
        return "NMX-A-7211/2-INNTEX-2015 DENSIDAD DE TEJIDO PLANO(MÉTODO A)"
      case "edit":
        return "Editar NMX-A-7211/2-INNTEX-2015 DENSIDAD DE TEJIDO PLANO(MÉTODO A)"
      case "view":
        return "Ver NMX-A-7211/2-INNTEX-2015 DENSIDAD DE TEJIDO PLANO(MÉTODO A)"
      default:
        return "NMX-A-7211/2-INNTEX-2015 DENSIDAD DE TEJIDO PLANO(MÉTODO A)"
    }
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackFlush}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
          <h1>{getTitle()}</h1>
        </div>
        <div className="flex gap-2">
          {/* Admin action buttons */}
          {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
            <>
              <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
              <Button variant="destructive" onClick={() => setRechazoModalOpen(true)}>Rechazar</Button>
              <Button variant="secondary" onClick={handleEditar}>Editar</Button>
            </>
          )}
          {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
            <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
          )}
          {/* Botón guardar en modo create */}
          {mode === 'create' && (
            <Button onClick={handleSave} className="gap-2" disabled={guardando}>
              <Save className="h-4 w-4" />
              {guardando ? 'Guardando...' : 'Guardar'}
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="text-center">
            <h2 className="text-xl font-semibold">NMX-A-7211/2-INNTEX-2015 </h2>
            <div className="text-base mt-1 mb-1">
      DETERMINACIÓN DEL NÚMERO DE HILOS POR UNIDAD DE LONGITUD (MÉTODO A)
    </div>
            <div className="mt-2 mb-2">
              <span className="inline-block px-2 py-1 text-xs font-semibold rounded bg-primary/10 text-primary">Método: MET-7.2-6</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Apartado Número de solicitud y Termohigrómetro */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="numero_solicitud">Número de solicitud</label>
              <Input
                id="numero_solicitud"
                type="text"
                placeholder="Ej. 12345"
                value={formData.numero_solicitud || ''}
                onChange={e => handleInputChange('numero_solicitud', e.target.value)}
                disabled={isReadOnly}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="id_termohigrometro">ID de termohigrómetro</label>
              <Select
                value={formData.id_termohigrometro || ''}
                onValueChange={value => handleInputChange('id_termohigrometro', value)}
                disabled={isReadOnly}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Seleccione..." />
                </SelectTrigger>
                <SelectContent>
                  {TERM_OPTIONS.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Tabla de información general */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-border text-sm">
              <tbody>
                {/* Primera fila */}
                <tr>
                  <td className="border border-border px-3 py-2 bg-muted font-medium w-20">FECHA</td>
                  <td className="border border-border p-2 w-32">
                    <Input
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => handleInputChange('fecha', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-sm h-7"
                    />
                  </td>
                  <td className="border border-border px-3 py-2 bg-muted font-medium w-20">HORA</td>
                  <td className="border border-border p-2 w-32">
                    <Input
                      type="time"
                      value={formData.hora}
                      onChange={(e) => { if (e.target.value) handleInputChange('hora', e.target.value) }}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-sm h-7"
                    />
                  </td>
                  <td className="border border-border px-3 py-2 bg-muted font-medium w-16">°C:</td>
                  <td className="border border-border p-2 w-24">
                    <Input
                      type="text"
                      value={formData.temperatura}
                      onChange={(e) => handleInputChange('temperatura', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-sm h-7"
                    />
                  </td>
                  <td className="border border-border px-3 py-2 bg-muted font-medium w-20">% HR:</td>
                  <td className="border border-border p-2 w-24">
                    <Input
                      type="text"
                      value={formData.humedad}
                      onChange={(e) => handleInputChange('humedad', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-sm h-7"
                    />
                  </td>
                </tr>
                
                {/* Segunda fila */}
                <tr>
                  <td className="border border-border px-3 py-2 bg-muted font-medium">ESPECÍMENES ENSAYADOS URDIMBRE:</td>
                  <td className="border border-border p-2">
                    <Input
                      type="text"
                      value={formData.especimenes_ensayados}
                      onChange={(e) => handleInputChange('especimenes_ensayados', e.target.value)}
                      disabled={isReadOnly}
                      placeholder="ESPECIMENES"
                      className="border-0 bg-transparent text-sm h-7"
                    />
                  </td>
                  <td className="border border-border px-3 py-2 bg-muted font-medium">ESPECÍMENES ENSAYADOS TRAMA:</td>
                  <td className="border border-border p-2">
                    <Input
                      type="text"
                      value={formData.especimenes_ensayados_trama || ''}
                      onChange={(e) => handleInputChange('especimenes_ensayados_trama', e.target.value)}
                      disabled={isReadOnly}
                      placeholder="ESPECIMENES"
                      className="border-0 bg-transparent text-sm h-7"
                    />
                  </td>
                  {/* Cambiado: etiquetas de distancia */}
                  <td className="border border-border px-3 py-2 bg-muted font-medium">Distancia de medición urdimbre</td>
                  <td className="border border-border p-2">
                    <select
                      value={formData.urdimbre}
                      onChange={(e) => handleInputChange('urdimbre', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-sm h-7"
                    >
                      <option value="">Selecciona distancia...</option>
                      {DISTANCIAS.map((d, idx) => (
                        <option key={idx} value={parseFloat(d)}>{d}</option>
                      ))}
                    </select>
                  </td>
                  <td className="border border-border px-3 py-2 bg-muted font-medium">Distancia de medición trama</td>
                  <td className="border border-border p-2">
                    <select
                      value={formData.trama}
                      onChange={(e) => handleInputChange('trama', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-sm h-7"
                    >
                      <option value="">Selecciona distancia...</option>
                      {DISTANCIAS.map((d, idx) => (
                        <option key={idx} value={parseFloat(d)}>{d}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Sección principal con tabla de mediciones */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tabla de mediciones */}
            <div className="lg:col-span-2">
              <div className="border border-border">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="border border-border px-4 py-2 bg-muted font-medium">NO.</th>
                      <th className="border border-border px-4 py-2 bg-muted font-medium">URDIMBRE</th>
                      <th className="border border-border px-4 py-2 bg-muted font-medium">TRAMA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3, 4, 5].map((num) => (
                      <tr key={num}>
                        <td className="border border-border px-4 py-2 text-center font-medium">{num}</td>
                        <td className="border border-border p-2">
                          <Input
                            type="text"
                            value={formData[`no${num}_urdimbre` as keyof NMXA72112FormData] as string}
                            onChange={(e) => handleInputChange(`no${num}_urdimbre` as keyof NMXA72112FormData, e.target.value)}
                            disabled={isReadOnly}
                            className="border-0 bg-transparent text-sm h-7"
                          />
                        </td>
                        <td className="border border-border p-2">
                          <Input
                            type="text"
                            value={formData[`no${num}_trama` as keyof NMXA72112FormData] as string}
                            onChange={(e) => handleInputChange(`no${num}_trama` as keyof NMXA72112FormData, e.target.value)}
                            disabled={isReadOnly}
                            className="border-0 bg-transparent text-sm h-7"
                          />
                        </td>
                      </tr>
                    ))}
                    
                    {/* Fila PROMEDIO */}
                    <tr>
                      <td className="border border-border px-4 py-2 text-center font-medium bg-muted">PROMEDIO</td>
                      <td className="border border-border p-2 bg-muted">
                        <Input
                          type="text"
                          value={formData.promedio_urdimbre}
                          onChange={(e) => handleInputChange('promedio_urdimbre', e.target.value)}
                          disabled={isReadOnly}
                          className="border-0 bg-transparent text-sm h-7 font-medium"
                          readOnly
                        />
                      </td>
                      <td className="border border-border p-2 bg-muted">
                        <Input
                          type="text"
                          value={formData.promedio_trama}
                          onChange={(e) => handleInputChange('promedio_trama', e.target.value)}
                          disabled={isReadOnly}
                          className="border-0 bg-transparent text-sm h-7 font-medium"
                          readOnly
                        />
                      </td>
                    </tr>
                    
                    {/* Fila RESULTADO */}
                    <tr>
                      <td className="border border-border px-4 py-2 text-center font-medium bg-muted">RESULTADO</td>
                      <td className="border border-border p-2 bg-muted">
                        <div className="flex items-center gap-1">
                          <Input
                            type="text"
                            value={formData.resultado_urdimbre}
                            onChange={(e) => handleInputChange('resultado_urdimbre', e.target.value)}
                            disabled={isReadOnly}
                            className="border-0 bg-transparent text-sm h-7 font-medium"
                            readOnly
                          />
                          <span className="text-xs text-muted-foreground">Hilos por cm</span>
                        </div>
                      </td>
                      <td className="border border-border p-2 bg-muted">
                        <div className="flex items-center gap-1">
                          <Input
                            type="text"
                            value={formData.resultado_trama}
                            onChange={(e) => handleInputChange('resultado_trama', e.target.value)}
                            disabled={isReadOnly}
                            className="border-0 bg-transparent text-sm h-7 font-medium"
                            readOnly
                          />
                          <span className="text-xs text-muted-foreground">Hilos por cm</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sección derecha - Realizó y Supervisó */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm text-center">REALIZÓ</CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    type="text"
                    value={formData.realizo}
                    onChange={(e) => handleInputChange('realizo', e.target.value)}
                    disabled
                    placeholder="Nombre del técnico"
                    className="text-sm h-8"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm text-center">SUPERVISÓ</CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    type="text"
                    value={formData.superviso}
                    onChange={(e) => handleInputChange('superviso', e.target.value)}
                    disabled
                    placeholder="Nombre del supervisor"
                    className="text-sm h-8"
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Observaciones */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm">OBSERVACIONES</CardTitle>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Mostrar en Reporte Cliente
                <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                  onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
              </span>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.observaciones}
                onChange={(e) => handleInputChange('observaciones', e.target.value)}
                disabled={isReadOnly}
                className="min-h-[100px] text-sm"
                placeholder="Ingrese observaciones adicionales..."
              />
            </CardContent>
          </Card>

          {/* Desviación al método */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">DESVIACIÓN AL MÉTODO</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.desviacion_metodo}
                onChange={(e) => handleInputChange('desviacion_metodo', e.target.value)}
                disabled={isReadOnly}
                className="min-h-[60px] text-sm"
                placeholder="Describa cualquier desviación al método..."
              />
            </CardContent>
          </Card>
        </CardContent>
      </Card>
      {/* Modal de rechazo */}
      <RechazoModal
        open={rechazoModalOpen}
        onClose={() => setRechazoModalOpen(false)}
        loading={rechazoLoading}
        onSubmit={(razon) => handleAccion('rechazado', razon)}
      />
    </div>
  )
}